import {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import { notifySessionExpired } from '../authSession';
import { clearTokenCache, getApiAccessToken } from '../services/tokenService';

export interface AuthAxiosRequestConfig extends InternalAxiosRequestConfig {
  _authRetry?: boolean;
}

/**
 * Attach auth header on requests and retry once after 401 with a forced token refresh.
 * Register this after other response interceptors so the retry runs first on errors.
 */
export function attachAuthInterceptors(instance: AxiosInstance): void {
  instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const token = await getApiAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as AuthAxiosRequestConfig | undefined;

      if (error.response?.status === 401 && config && !config._authRetry) {
        clearTokenCache();
        const token = await getApiAccessToken({ forceRefresh: true });
        if (token) {
          config._authRetry = true;
          config.headers.Authorization = `Bearer ${token}`;
          return instance.request(config);
        }
        notifySessionExpired();
      }

      return Promise.reject(error);
    }
  );
}
